/**
 * Organization selector component for the link flow.
 * Conditionally rendered based on feature flag and organization count.
 */

import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import * as Select from '@studio/design-system/select';
import type { AgentRegistryGetOrganizationResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { ReactElement } from 'react';

export interface OrganizationSelectorProps {
  /**
   * List of available organizations.
   */
  organizations: AgentRegistryGetOrganizationResponse[];

  /**
   * Currently selected organization ID.
   */
  selectedOrganizationId?: Uint8Array | undefined;

  /**
   * Callback when organization selection changes.
   * When organization changes, the room selection should be cleared.
   */
  onOrganizationChange: (organizationId: Uint8Array | undefined) => void;

  /**
   * Whether to show the selector.
   * Typically true when organizations.length > 1 and feature flag is enabled.
   */
  show?: boolean;
}

/**
 * Organization selector dropdown component.
 * Only renders when show is true and there are multiple organizations.
 *
 * @param props - Component props
 * @returns Organization selector or null
 */
export function OrganizationSelector({
  organizations,
  selectedOrganizationId,
  onOrganizationChange,
  show = false
}: OrganizationSelectorProps): ReactElement | null {
  if (!show || organizations.length <= 1) {
    return null;
  }

  return (
    <Select.Root
      onValueChange={(v: string): void => {
        const orgId = toIdFromHex(v);
        onOrganizationChange(orgId ?? undefined);
      }}
      size='xl'
      value={selectedOrganizationId ? toHexFromId(selectedOrganizationId) : undefined}
      variant='light'
    >
      <Select.Trigger
        className={selectedOrganizationId === undefined ? 'text-muted' : undefined}
        key={selectedOrganizationId === undefined ? 'trigger-placeholder' : 'trigger-active'}
      >
        <Select.Value placeholder='Select Organization' />
      </Select.Trigger>

      <Select.Content>
        {organizations.map((org) => (
          <Select.Item key={toHexFromId(org.id)} value={toHexFromId(org.id)!}>
            {org.name}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
}
